import { Obituary } from '../types/obituary';
import { Product } from '../enums';
import { getModelFromRef } from '../model';
import { safeGetModelArrayFromQuery } from '../model/getModel';
import { ObituaryModel } from '../model/objects/obituaryModel';
import { EFirebaseContext, ERef } from '../types';
import {
  AdditionalAdvertiserWithOrganizationOrderInfo,
  AdditionalAnonymousOrderInfo,
  AdditionalPublisherAsAnonymousOrderInfo,
  Order
} from '../types/order';
import { ResponseOrError, wrapError, wrapSuccess } from '../types/responses';
import { getErrorReporter } from '../utils/errors';
import { ColumnService } from './directory';
import { OrderService } from './orderService';

export class ObituaryService {
  private orderService: OrderService = new OrderService(this.context);

  constructor(private context: EFirebaseContext) {}

  async create(
    orderInfo:
      | AdditionalAnonymousOrderInfo
      | AdditionalAdvertiserWithOrganizationOrderInfo
      | AdditionalPublisherAsAnonymousOrderInfo
  ) {
    const initialVersion = this.context.timestamp().toMillis();
    const orderRef = await this.orderService.create(
      orderInfo,
      Product.Obituary,
      initialVersion
    );

    const obituaryRef = await this.context.obituariesRef().add({
      order: orderRef,
      orderVersion: initialVersion,
      deathVerification: {},
      createdAt: this.context.timestamp()
    });
    return getModelFromRef(ObituaryModel, this.context, obituaryRef);
  }

  public async getByOrderAndVersion(
    orderRef: ERef<Order>,
    version: number
  ): Promise<ResponseOrError<ObituaryModel>> {
    const query = this.context
      .obituariesRef()
      .where('order', '==', orderRef)
      .where('orderVersion', '==', version)
      .limit(2);
    const queryResult = await safeGetModelArrayFromQuery(
      ObituaryModel,
      this.context,
      query
    );

    if (queryResult.error) {
      return queryResult;
    }

    const adsForOrder = queryResult.response;

    if (adsForOrder.length !== 1) {
      const errorMessage = `Expected 1 obituary for order and version; instead got ${
        adsForOrder.length > 1 ? 'multiple' : '0'
      }`;

      const error = Error(errorMessage);

      /**
       * Since draft orders may not have an obituary yet, we should only
       * log this error to Sentry if there is more than one obituary for the order.
       */
      if (adsForOrder.length > 1) {
        getErrorReporter().logAndCaptureError(
          ColumnService.OBITS,
          error,
          errorMessage,
          {
            orderId: orderRef.id,
            version: `${version}`
          }
        );
      }

      return wrapError(error);
    }

    return wrapSuccess(adsForOrder[0]);
  }

  public async cloneForEditOrDuplicateFlow(
    antecedentObituaryId: string,
    antecedentObituary: Obituary,
    newVersion: number
  ): Promise<void> {
    const newObitRef = await this.context.obituariesRef().add({
      ...antecedentObituary,
      orderVersion: newVersion,
      createdAt: this.context.timestamp()
    });
    getErrorReporter().logInfo('Created new obituary for edit/duplicate flow', {
      antecedentObitId: antecedentObituaryId,
      newObitId: newObitRef.id,
      newVersion: `${newVersion}`
    });
  }
}
